import React, { useState } from 'react';
import axios from 'axios';
import ActionsDisplay from '../ActionsDisplay';
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Button } from 'reactstrap';
import ReportedListingDetails from '../ReportedListingDetails';
import ReportedUserDetails from '../ReportedUserDetails';
import SingleProduct from '../SingleProduct';
import EditUser from '../EditUser';
import { toast } from 'react-toastify';

const MyActions = ({ onLogout }) => {
    const [statusFilter, setStatusFilter] = useState('open');
    const [orderFilter, setOrderFilter] = useState('oldest');
    const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [entityData, setEntityData] = useState(null);
    const [loadingEntity, setLoadingEntity] = useState(false);
    const [updatingIssueStatus, setUpdatingIssueStatus] = useState(false);
    const [approvingUniversity, setApprovingUniversity] = useState(false);
    const [rejectingUniversity, setRejectingUniversity] = useState(false);
    const [showRejectConfirm, setShowRejectConfirm] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

    const handleStatusToggle = () => {
        setStatusFilter(prev => prev === 'open' ? 'all' : 'open');
    };

    const handleOrderToggle = () => {
        setOrderFilter(prev => prev === 'oldest' ? 'newest' : 'oldest');
    };

    const fetchEntityDetails = async (action) => {
        setLoadingEntity(true);
        setEntityData(null);
        try {
            let response;
            const token = localStorage.getItem('accessToken');
            const headers = {
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0',
            };

            switch (action.eventType) {
                case 'LISTING_REPORT':
                    let listingId = action.metadata?.listingId;
                    if (!listingId) {
                        toast.error('Missing listing information in action metadata.');
                        return;
                    }
                    response = await axios.get(
                        `${API_BASE_URL}/admin/reported-listings/${listingId}`,
                        { headers, params: { _t: Date.now() } }
                    );
                    setEntityData({ listing: { listingId: response.data.id, listing: response.data } });
                    break;

                case 'USER_REPORT':
                    let reportedUserId = action.metadata?.reportedUserId;
                    if (!reportedUserId) {
                        toast.error('Missing user information in action metadata.');
                        return;
                    }
                    response = await axios.get(
                        `${API_BASE_URL}/admin/reported-users/${reportedUserId}`,
                        { headers, params: { _t: Date.now() } }
                    );
                    setEntityData({ 
                        user: { 
                            userId: response.data.reportedUser?.id,
                            user: response.data.reportedUser,
                            reports: response.data.reports,
                            totalReports: response.data.totalReports,
                            overallStatus: response.data.overallStatus,
                            firstReportedAt: response.data.firstReportedAt,
                            lastReportedAt: response.data.lastReportedAt,
                        } 
                    });
                    break;

                case 'PRODUCT_LISTING':
                    response = await axios.get(
                        `${API_BASE_URL}/product/${action.eventId}`,
                        { headers, params: { _t: Date.now() } }
                    );
                    setEntityData(response.data);
                    break;

                case 'ISSUE':
                    response = await axios.get(
                        `${API_BASE_URL}/admin/issues/${action.eventId}`,
                        { headers, params: { _t: Date.now() } }
                    );
                    const issueData = response.data.data || {};
                    setEntityData({
                        ...response.data,
                        title: issueData.issueTitle || 'No Title',
                        description: issueData.issueDescription || 'No Description',
                        screenshots: issueData.issueScreenshots || [],
                    });
                    break;

                case 'USER_SIGNUP':
                    response = await axios.get(
                        `${API_BASE_URL}/admin/users/${action.eventId}`,
                        { headers, params: { _t: Date.now() } }
                    );
                    setEntityData(response.data);
                    break;

                case 'UNIVERSITY_APPROVAL':
                    // For university approval, eventId is the university name
                    // We don't need to fetch additional data, metadata has everything
                    setEntityData({
                        universityName: action.metadata?.universityName || action.eventId,
                        submittedBy: action.metadata?.submittedBy || null,
                        submittedAt: action.metadata?.submittedAt || action.createdAt,
                        actionId: action.id
                    });
                    break;

                default:
                    toast.error('Unknown event type');
                    return;
            }

            setIsOffcanvasOpen(true);
        } catch (err) {
            console.error('Failed to fetch entity details:', err);
            toast.error(err.message || err.response?.data?.error || 'Failed to fetch details');
            setIsOffcanvasOpen(false);
        } finally {
            setLoadingEntity(false);
        }
    };

    const handleViewAction = (action) => {
        setSelectedAction(action);
        fetchEntityDetails(action);
    };

    const toggleOffcanvas = () => {
        setIsOffcanvasOpen(!isOffcanvasOpen);
        if (!isOffcanvasOpen) {
            setEntityData(null);
            setSelectedAction(null);
        }
    };

    const handleEntitySuccess = () => {
        setRefreshKey(prev => prev + 1);
        toggleOffcanvas();
    };

    const renderEntityDetails = () => {
        if (loadingEntity) return <p>Loading details...</p>;
        if (!entityData || !selectedAction) return null;

        switch (selectedAction.eventType) {
            case 'LISTING_REPORT':
                return <ReportedListingDetails listing={entityData.listing} onEject={handleEntitySuccess} />;
            case 'USER_REPORT':
                return <ReportedUserDetails user={entityData.user} onResolve={handleEntitySuccess} />;
            case 'PRODUCT_LISTING':
                return <SingleProduct product={entityData} onSuccess={handleEntitySuccess} />;
            case 'ISSUE':
                const handleIssueStatusUpdate = async (status) => {
                    if (!entityData) return;
                    setUpdatingIssueStatus(true);
                    try {
                        const token = localStorage.getItem('accessToken');
                        const response = await axios.patch(
                            `${API_BASE_URL}/admin/issues/${entityData.id}/status`,
                            { status },
                            { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
                        );
                        setEntityData(prev => ({ ...prev, status: response.data.issue.status }));
                        toast.success('Issue status updated successfully');
                    } catch (err) {
                        toast.error(err.response?.data?.error || 'Failed to update issue status');
                    } finally {
                        setUpdatingIssueStatus(false);
                    }
                };
                return (
                    <div>
                        <div className="mb-3">
                            <h5>User Information</h5>
                            <p><strong>Name:</strong> {entityData.user?.fullName || 'N/A'}</p>
                            <p><strong>Email:</strong> {entityData.user?.email || 'N/A'}</p>
                            {entityData.user?.phoneNumber && <p><strong>Phone:</strong> {entityData.user.phoneNumber}</p>}
                            {entityData.user?.university && <p><strong>University:</strong> {entityData.user.university}</p>}
                        </div>
                        <hr />
                        <div className="mb-3">
                            <h5>Issue Information</h5>
                            <p><strong>Title:</strong> {entityData.title || 'N/A'}</p>
                            <p><strong>Status:</strong>
                                <span style={{
                                    marginLeft: '8px',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: entityData.status === 'Resolved' ? '#28a745' :
                                        entityData.status === 'Valid Bug' ? '#007bff' :
                                            entityData.status === 'Invalid Bug' ? '#dc3545' :
                                                entityData.status === 'Submitted' ? '#ffc107' : '#6c757d',
                                    color: 'white',
                                    fontSize: '12px'
                                }}>
                                    {entityData.status}
                                </span>
                            </p>
                            <p><strong>Created:</strong> {new Date(entityData.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="mb-3">
                            <h5>Mark Issue</h5>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleIssueStatusUpdate('Valid Bug')}
                                    disabled={updatingIssueStatus || entityData.status === 'Valid Bug'}
                                >
                                    Valid Bug
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={() => handleIssueStatusUpdate('Invalid Bug')}
                                    disabled={updatingIssueStatus || entityData.status === 'Invalid Bug'}
                                >
                                    Invalid Bug
                                </button>
                            </div>
                        </div>
                        <div className="mb-3">
                            <h5>Description</h5>
                            <p style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '4px', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                {entityData.description || 'No description provided'}
                            </p>
                        </div>
                        {entityData.screenshots && entityData.screenshots.length > 0 && (
                            <div className="mb-3">
                                <h5>Supporting Images ({entityData.screenshots.length})</h5>
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    {entityData.screenshots.map((screenshot, idx) => (
                                        <a key={idx} href={screenshot.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', textDecoration: 'none' }}>
                                            <img src={screenshot.fileUrl} alt={`Screenshot ${idx + 1}`} style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer' }} />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'USER_SIGNUP':
                return <EditUser user={entityData} onClose={toggleOffcanvas} onSuccess={handleEntitySuccess} />;
            case 'UNIVERSITY_APPROVAL':
                const handleApproveUniversity = async () => {
                    if (!entityData || !entityData.universityName) return;
                    setApprovingUniversity(true);
                    try {
                        const token = localStorage.getItem('accessToken');
                        const response = await axios.post(
                            `${API_BASE_URL}/admin/approve-university`,
                            { universityName: entityData.universityName },
                            { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
                        );
                        toast.success(response.data.message || 'University approved successfully');
                        handleEntitySuccess();
                    } catch (err) {
                        toast.error(err.response?.data?.error || 'Failed to approve university');
                    } finally {
                        setApprovingUniversity(false);
                    }
                };

                const handleRejectUniversity = async () => {
                    if (!entityData || !entityData.universityName) return;
                    setRejectingUniversity(true);
                    try {
                        const token = localStorage.getItem('accessToken');
                        const response = await axios.post(
                            `${API_BASE_URL}/admin/reject-university`,
                            { universityName: entityData.universityName },
                            { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
                        );
                        toast.success(response.data.message || 'University approval request rejected successfully');
                        setShowRejectConfirm(false);
                        handleEntitySuccess();
                    } catch (err) {
                        toast.error(err.response?.data?.error || 'Failed to reject university');
                    } finally {
                        setRejectingUniversity(false);
                    }
                };

                return (
                    <div>
                        <div className="mb-3">
                            <h5>University Approval Request</h5>
                            <p><strong>University Name:</strong> {entityData.universityName || 'N/A'}</p>
                            {entityData.submittedAt && (
                                <p><strong>Submitted At:</strong> {new Date(entityData.submittedAt).toLocaleString()}</p>
                            )}
                            {entityData.submittedBy && (
                                <p><strong>Submitted By User ID:</strong> {entityData.submittedBy}</p>
                            )}
                        </div>
                        <hr />
                        <div className="mb-3">
                            <h5>Action</h5>
                            <div className="d-flex gap-2">
                                <Button
                                    color="success"
                                    onClick={handleApproveUniversity}
                                    disabled={approvingUniversity || rejectingUniversity}
                                    className="flex-fill"
                                >
                                    {approvingUniversity ? 'Approving...' : 'Approve University'}
                                </Button>
                                <Button
                                    color="danger"
                                    onClick={() => setShowRejectConfirm(true)}
                                    disabled={approvingUniversity || rejectingUniversity}
                                    className="flex-fill"
                                >
                                    Reject
                                </Button>
                            </div>
                            {approvingUniversity && (
                                <p className="text-muted mt-2">Creating university and updating user records...</p>
                            )}
                        </div>

                        {/* Rejection Confirmation Modal */}
                        {showRejectConfirm && (
                            <div 
                                className="modal fade show d-block" 
                                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                                tabIndex="-1"
                            >
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Confirm Rejection</h5>
                                            <button 
                                                type="button" 
                                                className="btn-close" 
                                                onClick={() => setShowRejectConfirm(false)}
                                                disabled={rejectingUniversity}
                                            ></button>
                                        </div>
                                        <div className="modal-body">
                                            <p>Are you sure you want to reject the university approval request for:</p>
                                            <p><strong>{entityData.universityName}</strong></p>
                                            <p className="text-muted small">This will close the action item. The university will not be created.</p>
                                        </div>
                                        <div className="modal-footer">
                                            <button 
                                                type="button" 
                                                className="btn btn-secondary" 
                                                onClick={() => setShowRejectConfirm(false)}
                                                disabled={rejectingUniversity}
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-danger" 
                                                onClick={handleRejectUniversity}
                                                disabled={rejectingUniversity}
                                            >
                                                {rejectingUniversity ? 'Rejecting...' : 'Confirm Reject'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            default:
                return <p>Unknown entity type</p>;
        }
    };

    const getOffcanvasTitle = () => {
        if (!selectedAction) return 'Action Details';
        return selectedAction.metadata?.title || `${selectedAction.eventType.replace(/_/g, ' ')} Details`;
    };

    return (
        <>
            <div className='d-flex align-items-center justify-content-between mb-4'>
                <h2>My Actions</h2>
                <div className='d-flex align-items-center justify-content-end gap-3'>
                    <div className="btn-group" role="group" style={{ border: '1px solid #dee2e6', borderRadius: '50px', overflow: 'hidden', width: '240px' }}>
                        <button
                            type="button"
                            className={`btn ${orderFilter === 'oldest' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setOrderFilter('oldest')}
                            style={{
                                borderRadius: orderFilter === 'oldest' ? '50px 0 0 50px' : '0',
                                border: orderFilter === 'oldest' ? 'none' : '1px solid #667eea',
                                padding: '10px 24px',
                                fontWeight: orderFilter === 'oldest' ? '600' : '400',
                                height: '50px',
                                width: '50%',
                                background: orderFilter === 'oldest' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                color: orderFilter === 'oldest' ? 'white' : '#667eea'
                            }}
                        >
                            Oldest First
                        </button>
                        <button
                            type="button"
                            className={`btn ${orderFilter === 'newest' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setOrderFilter('newest')}
                            style={{
                                borderRadius: orderFilter === 'newest' ? '0 50px 50px 0' : '0',
                                border: orderFilter === 'newest' ? 'none' : '1px solid #667eea',
                                padding: '10px 24px',
                                fontWeight: orderFilter === 'newest' ? '600' : '400',
                                height: '50px',
                                width: '50%',
                                background: orderFilter === 'newest' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                color: orderFilter === 'newest' ? 'white' : '#667eea'
                            }}
                        >
                            Newest First
                        </button>
                    </div>
                    <div className="btn-group" role="group" style={{ border: '1px solid #dee2e6', borderRadius: '50px', overflow: 'hidden', width: '200px' }}>
                        <button
                            type="button"
                            className={`btn ${statusFilter === 'open' ? 'btn-primary' : 'btn-light'}`}
                            onClick={() => setStatusFilter('open')}
                            style={{
                                borderRadius: statusFilter === 'open' ? '50px 0 0 50px' : '0',
                                border: 'none',
                                padding: '10px 24px',
                                fontWeight: statusFilter === 'open' ? '600' : '400',
                                height: '50px',
                                width: '50%',
                                background: statusFilter === 'open' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                color: statusFilter === 'open' ? 'white' : '#495057'
                            }}
                        >
                            Open Only
                        </button>
                        <button
                            type="button"
                            className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-light'}`}
                            onClick={() => setStatusFilter('all')}
                            style={{
                                borderRadius: statusFilter === 'all' ? '0 50px 50px 0' : '0',
                                border: 'none',
                                padding: '10px 24px',
                                fontWeight: statusFilter === 'all' ? '600' : '400',
                                height: '50px',
                                width: '50%',
                                background: statusFilter === 'all' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                color: statusFilter === 'all' ? 'white' : '#495057'
                            }}
                        >
                            All
                        </button>
                    </div>
                </div>
            </div>

            <ActionsDisplay 
                key={refreshKey}
                statusFilter={statusFilter} 
                orderFilter={orderFilter}
                onViewAction={handleViewAction}
                filter="my"
            />

            <Offcanvas isOpen={isOffcanvasOpen} toggle={toggleOffcanvas} className="custom-canvas-width" direction="end">
                <OffcanvasHeader toggle={toggleOffcanvas}>
                    <span className='font-weight-bold'>{getOffcanvasTitle()}</span>
                </OffcanvasHeader>
                <OffcanvasBody>
                    {renderEntityDetails()}
                </OffcanvasBody>
            </Offcanvas>
        </>
    );
};

export default MyActions;

